import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import clsx from 'clsx'

/**
 * The one dialog chrome the CRM uses.
 *
 * Every CRM dialog had hand-rolled its own overlay, and none of them closed on
 * Escape, kept Tab inside, or gave focus back to the row that opened them. The
 * behaviour lives here once, copied from the dashboards' drill-down dialog —
 * the only implementation in the repository that did all of it — so a contact
 * window and a list editor behave identically to a keyboard.
 *
 * Portalled to <body> and the app shell marked `inert` while open: an
 * aria-modal dialog that leaves the page behind it in the accessibility tree
 * is one a screen reader walks straight out of.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Whether a control can take focus, as far as the DOM can say.
 *
 * `offsetParent` is the cheap test in a browser, but jsdom lays nothing out
 * and reports null for everything, which reduced the trap to "the element
 * that already has focus" under test. When layout says nothing, the computed
 * display of the control and its ancestors up to the dialog decides.
 */
function isRendered(el: HTMLElement, root: HTMLElement): boolean {
  if (el === document.activeElement) return true
  if (el.offsetParent !== null) return true
  let node: HTMLElement | null = el
  while (node && node !== root) {
    if (node.hidden) return false
    const style = window.getComputedStyle(node)
    if (style.display === 'none' || style.visibility === 'hidden') return false
    node = node.parentElement
  }
  return true
}

function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => isRendered(el, root))
}

/**
 * Whether this dialog is the one on top.
 *
 * The contact window opens the stored message over itself, and that viewer
 * is a modal dialog of its own. Both listen for Escape at document level, so
 * one keypress closed the viewer AND the window beneath it, and the window's
 * Tab trap kept pulling focus out of the viewer. A shell only acts on keys
 * while no later modal dialog is in the document.
 */
function isTopmost(dialog: HTMLElement | null): boolean {
  if (!dialog) return false
  const modals = document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')
  return modals[modals.length - 1] === dialog
}

/**
 * `inert` on the app shell, counted.
 *
 * Two shells open at once each set the attribute; the inner one closing
 * removed it while the outer was still up, and the page behind became
 * reachable again. A depth count means the attribute goes only when the last
 * shell does.
 */
const INERT_DEPTH = 'data-dialog-depth'
function holdInert(shell: HTMLElement): () => void {
  const depth = Number(shell.getAttribute(INERT_DEPTH) ?? '0') + 1
  shell.setAttribute(INERT_DEPTH, String(depth))
  shell.setAttribute('inert', '')
  shell.setAttribute('aria-hidden', 'true')
  return () => {
    const remaining = Number(shell.getAttribute(INERT_DEPTH) ?? '1') - 1
    if (remaining > 0) {
      shell.setAttribute(INERT_DEPTH, String(remaining))
      return
    }
    shell.removeAttribute(INERT_DEPTH)
    shell.removeAttribute('inert')
    shell.removeAttribute('aria-hidden')
  }
}

export type DialogWidth = 'md' | 'lg' | '2xl' | 'landscape'

const WIDTHS: Record<DialogWidth, string> = {
  md: 'w-full max-w-md max-h-[88vh]',
  lg: 'w-full max-w-lg max-h-[88vh]',
  '2xl': 'w-full max-w-2xl max-h-[88vh]',
  // A contact read whole: wide and short, the way a desk reads a file.
  landscape: 'w-[min(1680px,98vw)] h-[min(960px,94vh)]',
}

export default function DialogShell({
  label,
  onClose,
  width = 'lg',
  header,
  footer,
  children,
  zIndex = 80,
  testId,
}: {
  /** The accessible name — what `getByRole('dialog', { name })` finds. */
  label: string
  onClose: () => void
  width?: DialogWidth
  /** Replaces the default kicker + close row when the dialog has its own. */
  header?: ReactNode
  footer?: ReactNode
  children: ReactNode
  zIndex?: number
  testId?: string
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const restoreRef = useRef<HTMLElement | null>(null)
  // Read through a ref so an inline `onClose` — a new function on every
  // parent render — does not re-run the focus effect and yank the caret out
  // of whatever the person is typing into.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Escape closes, Tab cycles inside, and focus returns where it came from.
  useEffect(() => {
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? restoreRef.current
    // The dialog's own first control, when it has one; the close button
    // otherwise. Either way focus is inside the dialog, not behind it.
    const first = focusableWithin(dialogRef.current).find((el) => el !== closeRef.current)
    ;(first ?? closeRef.current)?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTopmost(dialogRef.current)) return
      if (event.key === 'Escape') {
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const nodes = focusableWithin(dialogRef.current)
      if (nodes.length === 0) return
      const head = nodes[0]
      const tail = nodes[nodes.length - 1]
      const active = document.activeElement
      const inside = dialogRef.current?.contains(active as Node) ?? false
      if (event.shiftKey && (!inside || active === head)) {
        event.preventDefault()
        tail.focus()
      } else if (!event.shiftKey && (!inside || active === tail)) {
        event.preventDefault()
        head.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    const restoreTo = restoreRef.current
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Deferred: the inert attribute below is still on #root when this
      // cleanup runs, and focus() on an inert subtree is silently ignored.
      setTimeout(() => restoreTo?.focus?.(), 0)
    }
  }, [])

  useEffect(() => {
    const shell = document.getElementById('root')
    if (!shell) return
    return holdInert(shell)
  }, [])

  const node = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      style={{ zIndex }}
      onClick={onClose}
      data-testid={testId}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={clsx(
          'card relative flex flex-col overflow-hidden shadow-2xl',
          WIDTHS[width],
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {header ?? (
          <div className="flex flex-shrink-0 items-center justify-between border-b border-amber-400/10 px-4 py-2.5">
            <span className="terminal-kicker">{label}</span>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded text-stone-500 hover:text-stone-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        {footer ? (
          <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-amber-400/10 px-4 py-2.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
  return typeof document === 'undefined' ? node : createPortal(node, document.body)
}
